import React, { useEffect, useMemo, useRef, useState } from "react";

import _ from "lodash";

import {
  CommonSortProps,
  CoreProps,
  updateSortState,
  Field,
  GridColumnSortState,
  GridSortState,
  GridSortStateEntry,
  SortColumn,
  SortOrder,
} from "./util";

import { useDeepEqualMemo, useQueryStringSort } from "utils/hooks";
import GridImpl from "./GridImpl";
import { wrap } from "utils/array";

export type AutoSortProps<T> = {
  data: T[];
  defaultSortBy?: Field<T> | Field<T>[];
  /** Query string parameter to use to persist the sort (default "s"), or
   `false` to avoid persisting the sort in the URL */
  sortParam?: string | false;
};

export type Props<T> = CoreProps<T> & AutoSortProps<T>;

function AutoSortGrid<T>(props: Props<T>) {
  const { defaultSortBy, sortParam, ...rest } = props;

  const { data, entries, doSort } = useAutoSort(props);

  function handleSort(field: keyof T, _order: SortOrder, _col: number) {
    // GridImpl passes the visible-column index, but the sort state is keyed by
    // the full columns array. Resolve via field so hidden columns
    // (width: false) don't cause an off-by-one shift in the sort target.
    const fullColIdx = props.columns.findIndex((c) => c.field === field);
    if (fullColIdx < 0) {
      return;
    }
    doSort(fullColIdx);
  }

  // sortedBy is only used to display the sort marker, so it's fine to just pass
  // the first field
  const sortIdx = entries?.[0]?.columnIdx;
  const sortedBy = sortIdx == null ? undefined : props.columns[sortIdx].field;
  const sortedOrder = entries?.[0]?.order;

  const implProps = {
    ...rest,
    data,
    sortedBy,
    sortedOrder,
    handleSort,
  };

  return <GridImpl {...implProps} />;
}

type UseAutoSortProps<T> = CommonSortProps<T> & AutoSortProps<T>;

function useAutoSort<T>(props: UseAutoSortProps<T>): GridSortState<T> {
  const { data, columns, defaultSortBy, sortParam } = props;

  const defaultSortByList = wrap(defaultSortBy);
  const defaultSortByFirst = defaultSortByList?.[0];

  const defaultSortDirection =
    columns.find((col) => col.field === defaultSortByFirst)?.defaultSortOrder ??
    "ASC";

  const [
    { sortBy: urlSortBy, sortDirection: urlSortDirection, fromURL },
    updateURLSort,
  ] = useQueryStringSort({
    param: sortParam ?? "s",
    sortDirection: defaultSortDirection,
    sortBy: defaultSortByFirst,
  });

  const initialSortBy = fromURL ? urlSortBy : defaultSortBy;
  const initialSortDirection = fromURL
    ? urlSortDirection
    : defaultSortDirection;

  const [sortState, setSortState] = useState<GridColumnSortState>(() => {
    return initialSortState({
      columns,
      initialSortBy: initialSortBy,
      initialSortDirection: initialSortDirection,
    });
  });

  const cachedDefaultSortBy = useDeepEqualMemo(defaultSortBy);
  const cachedSortColumns = useDeepEqualMemo(
    columns.map((col) => {
      return _.pick(col, "field", "defaultSortOrder");
    }),
  );
  // We want to reset the sort state when anything that affects the default sort
  // changes. Most commonly, this will be the visible column list on Grids that
  // can hide some columns. We don't care about URL sort order here, because if
  // these values changed, the URL sort order is by definition stale, so we
  // clear it. We track previous values via a ref to avoid resetting on the
  // initial render (which would clear a URL-specified sort order).
  const prevSortInputs = useRef({ cachedSortColumns, cachedDefaultSortBy });
  useEffect(() => {
    if (
      prevSortInputs.current.cachedSortColumns === cachedSortColumns &&
      prevSortInputs.current.cachedDefaultSortBy === cachedDefaultSortBy
    ) {
      return;
    }
    prevSortInputs.current = { cachedSortColumns, cachedDefaultSortBy };
    setSortState(
      initialSortState({
        columns: cachedSortColumns,
        initialSortBy: cachedDefaultSortBy,
      }),
    );
    updateURLSort(undefined);
  }, [cachedSortColumns, cachedDefaultSortBy, updateURLSort]);

  const sortedData = useMemo(() => {
    if (sortState.length === 0) {
      return data;
    }
    return doSort(data, cachedSortColumns, sortState);
  }, [data, cachedSortColumns, sortState]);

  function handleSort(sortColIdx: number): void {
    const newSortState = updateSortState(sortState, columns, sortColIdx);

    const newLeadCol = columns[newSortState[0].columnIdx].field;
    const newLeadOrder = newSortState[0].order;

    setSortState(newSortState);
    updateURLSort({ sortBy: newLeadCol, sortDirection: newLeadOrder });
  }

  return {
    data: sortedData,
    entries: sortState,
    doSort: handleSort,
  };
}

function initialSortState<T>({
  columns,
  initialSortBy,
  initialSortDirection,
}: {
  columns: readonly { field: keyof T; defaultSortOrder?: SortOrder }[];
  initialSortBy?: Field<T> | Field<T>[];
  initialSortDirection?: SortOrder;
}): GridColumnSortState {
  if (!initialSortBy) {
    return [];
  }

  const initialSortByList = wrap(initialSortBy);
  return initialSortByList
    .map((sortColName) => {
      return columns.findIndex((col) => col.field === sortColName);
    })
    .filter((colIdx) => {
      // ignore columns that don't exist on this data set
      return colIdx >= 0;
    })
    .map((colIdx) => {
      const defaultSortCol = columns[colIdx];
      const defaultSortOrder =
        initialSortDirection ?? defaultSortCol?.defaultSortOrder ?? "ASC";
      return {
        columnIdx: colIdx,
        order: defaultSortOrder,
      };
    });
}

function doSort<T>(
  data: T[],
  columns: readonly SortColumn<T, keyof T>[],
  entries: GridSortStateEntry[],
): T[] {
  const newComparator = makeSortComparator(entries, columns);
  return data.slice().sort(newComparator);
}

/**
 * This returns a function that conforms to the Array#sort interface for sorting
 * grid data. Values that are null-like (see sortAsUndefined) always sort last
 * according to the default sort order of the column (i.e., if a column sorts
 * descending by default, null will be the lowest value; if ascending by
 * default, null will be the highest value). If values for the given items in
 * the most-recently sorted-by column are equal, values in the
 * next-most-recently sorted-by are compared.
 */
function makeSortComparator<T>(
  entries: GridSortStateEntry[],
  columns: readonly SortColumn<T, keyof T>[],
): (item1: T, item2: T) => number {
  return (item1, item2) => {
    for (const entry of entries) {
      const column = columns[entry.columnIdx];

      if (!column) {
        // Should not happen, but if we do hit this, ignore this missing column
        // rather than erroring out
        return 0;
      }

      const sortVal1 = sortValue(item1, column);
      const sortVal2 = sortValue(item2, column);

      const asc = entry.order === "ASC";
      const nullsLast = (column.defaultSortOrder ?? "ASC") == entry.order;
      if (sortVal1 === sortVal2) {
        continue;
      } else if (sortVal1 === undefined) {
        return nullsLast ? 1 : -1;
      } else if (sortVal2 === undefined) {
        return nullsLast ? -1 : 1;
      } else if (sortVal1 < sortVal2) {
        return asc ? -1 : 1;
      } else if (sortVal1 > sortVal2) {
        return asc ? 1 : -1;
      }
    }
    return 0;
  };
}

function sortValue<T>(
  row: T,
  column: SortColumn<T, keyof T>,
): string | number | boolean | undefined {
  const rawVal = row[column.field];
  if (sortAsUndefined(rawVal)) {
    return undefined;
  }
  if (
    typeof rawVal === "number" ||
    typeof rawVal === "string" ||
    typeof rawVal === "boolean"
  ) {
    return rawVal;
  }

  return String(rawVal);
}

function sortAsUndefined(value: unknown) {
  return (
    value === null ||
    value === undefined ||
    value === "" ||
    (typeof value === "number" && isNaN(value))
  );
}

export default AutoSortGrid;
